import { formatLocation, getHost, getServiceLabel } from '../utilities/service.ts'
import { isNodeOffline } from '../utilities/nodeStatus.ts'
import { NODE_KIND } from './tooltip.ts'
import { leafVisible } from './leaves.ts'
import { applySearchFilter, visibleIdsSignature } from './search.ts'
import { NODE_MARK_TITLES, cloudPlaceId, cloudPlaceOf, nodeMarks } from '../utilities/nodeMarks.ts'
import { encodeArcs, networkHues } from './networkHues.ts'

/**
 * The hub of the nodes in no network, on the graph (app#498). It stands where a network stands in
 * the chain, so a node in no network has a location and a link, as every node has. It is no
 * network: it has no `routeId`, and it joins no highlight. The map does not draw it.
 */
export const NO_NETWORK_HUB = 'no-network'

/** The id of the location that a place is on the map, for all networks: see `onePerPlace`. */
const placeId = (location: string) => `place:${location}`

export default class Adapter {
  nodeMap: Map<string, GraphNode> = new Map()
  linkMap: Map<string, GraphLink> = new Map()
  /**
   * The nodes the reader is pointing at — hovered or selected — which is what decides whether a
   * hidden leaf is drawn. Published because `core.ts` pins on it too, and the two must not
   * disagree about which node that is; computing it twice is how they would.
   */
  focusIds: Set<string> = new Set()
  /**
   * ONE location for each place, for all networks: the map (app#498). A machine is in one place, so
   * a city has one ring however many networks have a node there. The location's id then holds no
   * network, which is what a cloud location already does. A node in no network has no hub here: it
   * sits on the ring of its place.
   * ⚠ The graph keeps one location for each network: there a location is a step of the chain
   * Network → Location → Node.
   */
  private readonly onePerPlace: boolean
  private lastVisibleSignature = ''
  private lastHadQuery = false

  constructor({ onePerPlace = false }: { onePerPlace?: boolean } = {}) {
    this.onePerPlace = onePerPlace
  }

  transform(
    data: MergedData,
    activeId?: string,
    selectedId?: string,
    query?: string
  ): boolean {
    let rerender = false

    this.nodeMap.forEach(node => (node.stale = false))
    this.linkMap.forEach(link => (link.stale = false))

    const nodeMetadata = new Map<string, NodeMetadata>()
    data.nodeMetadata.forEach(metadata => {
      nodeMetadata.set(metadata.nodeId, metadata)
    })

    // ⚠⚠ A NODE OF A CLOUD IS IN THE CLOUD'S LOCATION, NOT AT ITS PLACE (Benoit, 2026-10-20: "they
    // do not appear in there actual true location, but instead are grouped by cloud type"). Its
    // location key is the cloud's id, so its network's link goes there; the location itself is ONE
    // node for every network (`cloudPlaceId`), drawn as the cloud's icon. Its own mark for that
    // cloud is left off its name: the location says it. The node list keeps it (`NodeMarks.tsx`).
    const allNodes = [...data.nodes, ...(data.noNetworkNodes ?? [])]
    const marksOf = new Map(
      allNodes.map(node => [node.id, nodeMarks({ ...nodeMetadata.get(node.id), provisioned: !!data.compute?.has(node.id) })])
    )
    const cloudOf = new Map(allNodes.map(node => [node.id, cloudPlaceOf(marksOf.get(node.id) ?? [])]))
    const locationKeys = new Map<string, string>()
    allNodes.forEach(node => {
      const cloud = cloudOf.get(node.id)
      locationKeys.set(node.id, cloud ? cloudPlaceId(cloud) : formatLocation(nodeMetadata.get(node.id)))
    })

    // @TODO: check we need this now that there are no proxy/gateway nodes
    const findLocations = (s: Session) =>
      allNodes
        .filter(n => n.id === s.proxyId || n.id === s.gatewayId)
        .map(n => locationKeys.get(n.id))
        .filter(addr => addr) as string[]

    const ids: Set<string> = new Set()
    const nodes = new Set(allNodes.map(n => n.id))
    const locations = new Set(allNodes.map(n => locationKeys.get(n.id)).filter(addr => addr) as string[])
    const serviceActive = data.services.find(s => s.id === activeId)
    const nodeActive = !!activeId && (nodes.has(activeId) || serviceActive)
    const activeNodes = new Set([activeId, serviceActive?.nodeId].filter(Boolean))

    const streamingNodes = new Map(
      data.sessions
        .filter(s => s.rxBytes || s.txBytes)
        .flatMap(s => {
          const locations = findLocations(s)
          return [
            s.proxyId,
            s.gatewayId,
            s.networkId,
            ...locations,
            ...locations.map(location => `${s.networkId}:${location}`),
          ].map(id => [id, s])
        })
    )
    const streamingLocationCount = Array.from(locations).filter(item => streamingNodes.has(item)).length

    const connectedNodes = new Set(data.sessions.flatMap(s => [s.proxyId, findLocations(s), s.gatewayId, s.networkId]))

    // Every service row stays loaded, and only the services of the node being pointed at are
    // drawn. `leaves.ts` owns the rule, and says why a search draws them all.
    //
    // ⚠ `focusIds` is hover AND selection. `activeNodes` already resolves a hovered SERVICE back
    // to the node it sits on, which is why it is the set used and not `activeId`.
    const searching = !!query?.trim()
    this.focusIds = new Set([...activeNodes, selectedId].filter(Boolean) as string[])
    const focus = this.focusIds
    // A node being pointed at dims the labels of the rest.
    const dimLabels = !!nodeActive

    // The networks of the highlighted node, each with its color (app#498): the one under the
    // pointer, else the selected one. ⚠⚠ A PEER DOES NOT JOIN `focusIds`: that set also decides
    // which services are drawn (`leaves.ts`), and which names show. A peer gets its arcs, its
    // services stay hidden and its name stays hidden: on a large network the names would crowd
    // the map.
    const highlighted = allNodes.find(n => activeNodes.has(n.id)) ?? allNodes.find(n => n.id === selectedId)
    const hues = networkHues(highlighted?.networkIds ?? [], data.networks)
    const arcsOf = (node: NodeRecord) =>
      encodeArcs(node.networkIds.flatMap(id => (hues.has(id) ? [hues.get(id)!] : [])))

    // Accounts
    data.accounts.forEach(a => {
      addOrUpdate(this.nodeMap, a.id, {
        id: a.id,
        type: 'account',
        connected: false,
        label: a.name,
        url: a.url,
        offline: !!a.offlineAt,
        active: a.id === activeId,
        selected: selectedId === a.id,
      })
    })

    data.networks.forEach(network => {
      if (!network.id || !network.ownerId) return
      const linkId = `${network.ownerId}:${network.id}`

      // Account -> Network
      addOrUpdate(this.linkMap, linkId, {
        id: linkId,
        active: false,
        connected: false,
        source: network.ownerId,
        target: network.id,
        sourceType: 'account',
        targetType: 'network',
      })

      // Network
      addOrUpdate(this.nodeMap, network.id, {
        id: network.id,
        routeId: network.id,
        type: 'network',
        label: network.name,
        offline: !!network.offlineAt,
        active: network.id === activeId,
        streaming: streamingLocationCount > 1 && streamingNodes.has(network.id),
        connected: connectedNodes.has(network.id),
        selected: network.id === selectedId,
        // ⚠ Always written, so a highlight that ends clears it.
        hue: hues.get(network.id),
      })
    })

    // A network of another org is not drawn, and a link to it would have no end.
    const drawnNetworks = new Set(data.networks.flatMap(n => (n.id && n.ownerId ? [n.id] : [])))

    if (data.noNetworkNodes?.length && !this.onePerPlace) {
      const account = data.accounts[0]
      addOrUpdate(this.nodeMap, NO_NETWORK_HUB, {
        id: NO_NETWORK_HUB,
        type: 'network',
        label: 'No network',
        noNetwork: true,
        offline: false,
        connected: false,
      })
      if (account) {
        const linkId = `${account.id}:${NO_NETWORK_HUB}`
        addOrUpdate(this.linkMap, linkId, {
          id: linkId,
          active: false,
          connected: false,
          source: account.id,
          target: NO_NETWORK_HUB,
          sourceType: 'account',
          targetType: 'network',
        })
      }
    }

    const streaming: Record<string, boolean> = {}
    const connected: Record<string, boolean> = {}

    allNodes.forEach(node => {
        const locationKey = locationKeys.get(node.id) || 'Unknown'
        const cloud = cloudOf.get(node.id)

        // ⚠ POINTED AT IS HOVERED **OR** SELECTED, and it is the same set that decides what is
        // DRAWN (`focusIds`). Highlighting off a different set is how the two came to disagree:
        // a selected node drew its leaves and then left every line to them the same grey as the
        // rest of the canvas, so the picture answered a question it did not look like it was
        // answering.
        const focusNode = focus.has(node.id)

        const streamingNode = streamingNodes.get(node.id)

        let targetType = 'node'
        if (streamingNode) {
          if (streamingNode?.gatewayId === node.id) {
            targetType = 'gateway'
          } else if (streamingNode?.proxyId === node.id) {
            targetType = 'proxy'
          }
        }

        const connectedNode = connectedNodes.has(node.id)

        // ⚠⚠ ONE GLYPH FOR A NODE, WITH ONE LINK FROM THE LOCATION OF EACH OF ITS NETWORKS (app#498).
        // Its id is the node's: `node:network` would draw a node twice. With several parents the
        // layout puts it between them (`shareAngles` in `layout.ts`). A node in no network is in the
        // chain of the hub on the graph; on the map it only has a location.
        const memberships = node.networkIds.filter(id => drawnNetworks.has(id))
        ;(memberships.length ? memberships : [NO_NETWORK_HUB]).forEach(networkId => {
          const noNetwork = networkId === NO_NETWORK_HUB
          const shared = !!cloud || this.onePerPlace

          // The place in this network. ⚠ A location shared by all networks (a cloud, or a place on
          // the map) is one node, and its states are those of all of them (`location`); its link
          // from THIS network is lit by the nodes of this network (`ownLocation`).
          const ownLocation = `${locationKey}:${networkId}`
          const networkLocation = `${networkId}:${locationKey}`
          // The id of the location node. The link to it from the network keeps `networkLocation`.
          const locationId = cloud ? locationKey : shared ? placeId(locationKey) : networkLocation
          const location = shared ? locationId : ownLocation
          const streamingNetworkLocation = streamingNodes.get(networkLocation)
          const connectedNetwork = connectedNodes.has(networkId)

          streaming[location] = streaming[location] || !!streamingNode
          streaming[networkLocation] =
            streamingLocationCount > 1 && (streaming[networkLocation] || !!streamingNetworkLocation)

          connected[location] = connected[location] || connectedNode
          connected[ownLocation] = connected[ownLocation] || connectedNode
          connected[networkLocation] = connected[networkLocation] || (connectedNetwork && connected[ownLocation])

          // Network -> Location
          if (!(noNetwork && this.onePerPlace))
            addOrUpdate(
              this.linkMap,
              networkLocation,
              {
                id: networkLocation,
                connected: connected[networkLocation],
                streaming: streaming[networkLocation],
                rxBytes: streamingNetworkLocation?.rxBytes ?? 0,
                txBytes: streamingNetworkLocation?.txBytes ?? 0,
                source: networkId,
                target: locationId,
                sourceType: 'network',
                targetType: 'node',
                // ⚠ Always written, so a highlight that ends clears it.
                hue: hues.get(networkId),
              },
              connected[networkLocation]
            )

          // Locations
          addOrUpdate(
            this.nodeMap,
            locationId,
            {
              id: locationId,
              type: 'location',
              label: cloud ? NODE_MARK_TITLES[cloud] : locationKey,
              ...(cloud ? { cloudPlace: cloud } : {}),
              streaming: streaming[location],
              connected: connected[location],
            },
            connected[location]
          )

          // Location -> Node
          const locationNode = `${locationId}>${node.id}`
          addOrUpdate(this.linkMap, locationNode, {
            id: locationNode,
            connected: connectedNode,
            streaming: !!streamingNode,
            rxBytes: streamingNode?.rxBytes ?? 0,
            txBytes: streamingNode?.txBytes ?? 0,
            source: locationId,
            target: node.id,
            sourceType: 'location',
            targetType: targetType,
          })
        })

        // Nodes
        addOrUpdate(this.nodeMap, node.id, {
          id: node.id,
          modelNodeId: node.id,
          type: 'node',
          label: node.name,
          kind: NODE_KIND,
          hideLabel: dimLabels && !focusNode,
          offline: isNodeOffline(node),
          active: focusNode,
          streaming: !!streamingNode,
          connected: connectedNode,
          selected: selectedId === node.id,
          // ⚠ Always written, `undefined` included: a task that stops must CLEAR the hover line, and
          // a key left out of this object is never compared, so the old text would stay.
          compute: data.compute?.get(node.id)?.label,
          // ⚠ Always written too, so a mark that goes is cleared (nobgp-router#2010).
          marks:
            (marksOf.get(node.id) ?? []).filter(mark => mark !== cloud).join(',') || undefined,
          // ⚠ Always written too, so a highlight that ends clears them.
          arcs: arcsOf(node),
        })

        // Services. ⚠⚠ A SERVICE IS DRAWN NAMED OR NOT AT ALL. It used to be emitted always and
        // collapsed to a bare 3 px circle with a line to it and no label — the reader could see
        // that a node had *something* and never what, and a fleet drew one such line per (service,
        // node) across the middle of the picture. `expanded` therefore no longer means "drawn
        // small": every service that reaches the map carries its icon and its label.
        const serviceShown = leafVisible(node.id, { searching, focus })

        if (serviceShown)
          data.services
            .filter(service => service.nodeId === node.id)
            .forEach(service => {
              const serviceId = service.id
              const serviceActive = serviceId === activeId
              // Node -> Service
              addOrUpdate(this.linkMap, `${node.id}:${serviceId}`, {
                id: `${node.id}:${serviceId}`,
                expanded: true,
                // Pointing at the node lights every service line out of it, so pointing at ONE
                // service lights its siblings too: what is pointed at is the node they hang off.
                active: focusNode,
                connected: service.enabled,
                streaming: !!streamingNode,
                source: node.id,
                target: serviceId,
                sourceType: 'node',
                targetType: 'service',
              })

              // Service
              addOrUpdate(this.nodeMap, serviceId, {
                id: serviceId,
                modelNodeId: node.id,
                routeId: service.name,
                type: 'service',
                label: getServiceLabel(service),
                hideLabel: false,
                link: service.url ? getHost(service) : undefined,
                offline: isNodeOffline(node) || !service.enabled,
                expanded: true,
                active: serviceActive || focusNode,
                streaming: !!streamingNode,
                connected: connectedNode,
              })
            })
      })

    // Remove links and nodes. ⚠ A removal is a rerender too: the wedges are apportioned by
    // subtree weight (`graph/layout.ts`), so losing a node changes where every one of its
    // siblings belongs — and without the flag the simulation is never restarted to take them
    // there. Reachable on any node leaving the filter.
    Array.from(this.linkMap.keys()).forEach(key => {
      if (!ids.has(key)) {
        this.linkMap.delete(key)
        rerender = true
      }
    })
    Array.from(this.nodeMap.keys()).forEach(key => {
      if (!ids.has(key)) {
        this.nodeMap.delete(key)
        rerender = true
      }
    })

    applySearchFilter(this.nodeMap, this.linkMap, query ?? '')

    const hasQuery = !!query?.trim()
    if (hasQuery || this.lastHadQuery) {
      const signature = visibleIdsSignature(this.nodeMap)
      if (signature !== this.lastVisibleSignature) {
        this.lastVisibleSignature = signature
        rerender = true
      }
    }
    this.lastHadQuery = hasQuery

    /*
    Add or update an item in a map
      @param map - The map to update
      @param id - The item id
      @param data - The item data
      @param forceUpdate - Force update the item
   */
    function addOrUpdate<T extends GraphNode | GraphLink>(
      map: Map<string, T>,
      id: string,
      data: Partial<T>,
      forceUpdate?: boolean
    ) {
      ids.add(id)

      if (map.has(id)) {
        const existingItem = map.get(id)!
        let stale = existingItem.stale

        Object.keys(data).forEach(key => {
          const typedKey = key as keyof T
          if (forceUpdate || existingItem[typedKey] !== data[typedKey]) {
            existingItem[typedKey] = data[typedKey] as T[keyof T]
            stale = true
          }
        })

        existingItem.stale = stale
        return
      }

      map.set(id, { ...data, stale: false } as T)
      rerender = true
    }

    return rerender
  }
}
